'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { AbstractIntlMessages } from 'next-intl'
import { useSearchParams } from 'next/navigation'

import MantineProvider from '@/components/providers/mantine'
import { LocaleProvider } from '@/lib/locale'
import { LOCALE_PARAM_KEY } from '@/lib/locale/locale.constants'
import type { Locale } from '@/lib/locale/locale.types'
import { ThemePreferences } from '@/types/platform'

type ChartsProvidersProps = Readonly<{
  children: React.ReactNode
  initialTheme: ThemePreferences
  initialLocale: Locale
  initialMessages: AbstractIntlMessages
}>

// Dedicated client for the public renderer — the account app's providers are intentionally skipped on the
// charts subdomain (bare root layout), so the renderer brings its own QueryClient.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, staleTime: Infinity, retry: false },
  },
})

const ChartsProviders = (props: ChartsProvidersProps) => {
  const { children, initialTheme, initialLocale, initialMessages } = props

  // Same resolution as the main Providers: a `?locale=` override wins, and then the
  // server messages belong to another locale, so they are not passed on.
  const searchParams = useSearchParams()
  const paramLocale = (searchParams.get(LOCALE_PARAM_KEY) as Locale) || undefined
  const locale = paramLocale ?? initialLocale
  const messages = locale === initialLocale ? initialMessages : undefined

  return (
    <MantineProvider initialTheme={initialTheme}>
      <LocaleProvider initialLocale={locale} initialMessages={messages}>
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
      </LocaleProvider>
    </MantineProvider>
  )
}

export default ChartsProviders
